import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChessBoard,
  faChessKnight,
  faChessRook,
  faChevronDown,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import { SendIcon } from "lucide-react";
import { Switch } from "@headlessui/react";
import { AnimatePresence, motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fakeUsers, remindersAndEvents } from "@/views/home/calendars/mocks/calendarMocks";
import { Link } from "react-router";

const partyIcons = {
  Claimant: <FontAwesomeIcon className="mr-2 h-5 w-5 bg-red-100 p-2 text-black" icon={faChessRook} />,
  Defendant: <FontAwesomeIcon className="mr-2 h-5 w-5 bg-blue-200 p-2 text-black" icon={faChessKnight} />,
  "All Parties": <FontAwesomeIcon className="mr-2 h-5 w-5 bg-green-100 p-2 text-green-800" icon={faChessBoard} />,
};
export type ModalTableProps = {
  toggleModal: () => void;
};
const getDayOfWeek = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", { weekday: "long" });
};

const isWeekend = (dateString: string) => {
  const date = new Date(dateString);
  const day = date.getDay();
  return day === 0 || day === 6;
};

const datePrefixes = ["2 weeks before", "1 week before", "72h before", "48h before"];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(" ");
}

const formatDateWithoutTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString();
};

export const ModalTable: React.FC<ModalTableProps> = ({ toggleModal }) => {
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const rowsPerPage = 8;

  const [selectedCalendarReminders, setSelectedCalendarReminders] = useState<string[]>([]);
  const [selectedTextReminders, setSelectedTextReminders] = useState<string[]>([]);

  const handleRowClick = (id: string) => {
    if (expandedRows.includes(id)) {
      setExpandedRows(expandedRows.filter(rowId => rowId !== id));
    } else {
      setExpandedRows([...expandedRows, id]);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      const allCalendarReminders = remindersAndEvents.map(item => item.id);
      const allTextReminders = remindersAndEvents.map(item => item.id);
      setSelectedCalendarReminders(allCalendarReminders);
      setSelectedTextReminders(allTextReminders);
    } else {
      setSelectedCalendarReminders([]);
      setSelectedTextReminders([]);
    }
  };

  const handleEventSelect = (id: string, type: "calendar" | "text") => {
    const eventReminders = remindersAndEvents.filter(reminder => reminder.parentId === id).map(reminder => reminder.id);
    if (type === "calendar") {
      setSelectedCalendarReminders(prev => {
        if (prev.includes(id)) {
          return prev.filter(reminderId => ![id, ...eventReminders].includes(reminderId));
        } else {
          return [...new Set([...prev, id, ...eventReminders])];
        }
      });
    } else {
      setSelectedTextReminders(prev => {
        if (prev.includes(id)) {
          return prev.filter(reminderId => ![id, ...eventReminders].includes(reminderId));
        } else {
          return [...new Set([...prev, id, ...eventReminders])];
        }
      });
    }
  };

  const handleReminderSelect = (id: string, type: "calendar" | "text") => {
    if (type === "calendar") {
      setSelectedCalendarReminders(prev =>
        prev.includes(id) ? prev.filter(reminderId => reminderId !== id) : [...prev, id],
      );
    } else {
      setSelectedTextReminders(prev =>
        prev.includes(id) ? prev.filter(reminderId => reminderId !== id) : [...prev, id],
      );
    }
  };

  const filteredEvents = remindersAndEvents
    .filter(
      event =>
        event.type === "event" &&
        (event.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          event.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
          new Date(event.datetime).toLocaleString().includes(searchTerm) ||
          event.assignedUserIds.some(uid =>
            fakeUsers
              .find(user => user.id === uid)
              ?.name.toLowerCase()
              .includes(searchTerm.toLowerCase()),
          )),
    )
    .sort((a, b) => new Date(a.datetime).getTime() - new Date(b.datetime).getTime());

  const paginatedEvents = filteredEvents.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  const pageCount = Math.ceil(filteredEvents.length / rowsPerPage);

  useEffect(() => {
    handleSelectAll(true);
  }, []);

  return (
    <div className="mx-auto mb-2 ml-8 mr-8 mt-4 min-h-lvh w-full min-w-[70vw] max-w-[70vw] overflow-auto bg-[#eaebe6]">
      <div className="mb-4 text-[#495853]">
        <h2 className="text-3xl">Check events & reminders before sending</h2>
        <p className="font-light text-[#545550]">Review and select reminders to send</p>
      </div>

      <div className="mb-12 mt-8 flex items-center justify-between">
        <div className="mr-4 w-full">
          <input
            type="text"
            placeholder="Search by person, event, reminder, date"
            value={searchTerm}
            onChange={handleSearchChange}
            className="mr-2 min-w-full rounded border border-gray-300 p-2"
          />
        </div>
        <Button onClick={() => handleSelectAll(selectedCalendarReminders.length !== remindersAndEvents.length)}>
          Select All
        </Button>
      </div>
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Date</th>
            <th className="w-1/3 px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Event Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Parties</th>
            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
              Responsible
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
              Calendar Reminder
            </th>
            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
              Text Reminder
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-[#fafafa]">
          {paginatedEvents.map(event => (
            <React.Fragment key={event.id}>
              <tr>
                <td
                  className="cursor-pointer whitespace-nowrap px-6 py-4 text-[#495853]"
                  onClick={() => handleRowClick(event.id)}
                >
                  {expandedRows.includes(event.id) ? (
                    <FontAwesomeIcon icon={faChevronDown} className="mr-2 text-xs" />
                  ) : (
                    <FontAwesomeIcon icon={faChevronRight} className="mr-2 text-xs" />
                  )}
                  {formatDateWithoutTime(event.datetime)}
                  <br />
                  <span
                    className={classNames("text-sm", isWeekend(event.datetime) ? "text-red-500" : "text-indigo-400")}
                  >
                    {getDayOfWeek(event.datetime)}
                  </span>
                  <br />
                  <span className="cursor-pointer text-xs text-[#495853]">{event.location}</span>
                </td>
                <td
                  className="text-md w-1/3 cursor-pointer whitespace-normal px-6 py-4 font-medium"
                  onClick={() => handleRowClick(event.id)}
                >
                  <div>{event.title}</div>
                  <div className="mt-1 pt-2">
                    <span className="block text-xs text-gray-400" style={{ maxWidth: "700px", wordWrap: "break-word" }}>
                      {event.summary}
                    </span>
                  </div>
                </td>

                <td className="flex items-center whitespace-nowrap px-6 py-4 pb-0 pt-5 text-sm">
                  {partyIcons[event.party]} {event.party}
                </td>
                <td className="whitespace-nowrap px-6 py-4 pt-3 text-sm">
                  <div className="flex items-center space-x-2">
                    {event.assignedUserIds.map(uid => {
                      const user = fakeUsers.find(user => user.id === uid);
                      return user ? (
                        <img
                          key={user.id}
                          src={user.avatar}
                          alt={user.name}
                          width="60"
                          height="60"
                          className="h-6 w-6 rounded-full"
                        />
                      ) : null;
                    })}
                  </div>
                </td>

                <td className="cursor-pointer whitespace-nowrap px-6 py-4 pt-3 text-center">
                  <Switch
                    checked={selectedCalendarReminders.includes(event.id)}
                    onChange={() => handleEventSelect(event.id, "calendar")}
                    className={classNames(
                      selectedCalendarReminders.includes(event.id) ? "bg-indigo-600" : "bg-gray-200",
                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                    )}
                  >
                    <span className="sr-only">Calendar Reminder</span>
                    <span
                      className={classNames(
                        selectedCalendarReminders.includes(event.id) ? "translate-x-5" : "translate-x-0",
                        "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                      )}
                    >
                      <span
                        className={classNames(
                          selectedCalendarReminders.includes(event.id)
                            ? "opacity-0 duration-100 ease-out"
                            : "opacity-100 duration-200 ease-in",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                        )}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                          <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        className={classNames(
                          selectedCalendarReminders.includes(event.id)
                            ? "opacity-100 duration-200 ease-in"
                            : "opacity-0 duration-100 ease-out",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                        )}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                        </svg>
                      </span>
                    </span>
                  </Switch>
                </td>
                <td className="cursor-pointer whitespace-nowrap px-6 py-4 pt-3 text-center">
                  <Switch
                    checked={selectedTextReminders.includes(event.id)}
                    onChange={() => handleEventSelect(event.id, "text")}
                    className={classNames(
                      selectedTextReminders.includes(event.id) ? "bg-indigo-600" : "bg-gray-200",
                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                    )}
                  >
                    <span className="sr-only">Text Reminder</span>
                    <span
                      className={classNames(
                        selectedTextReminders.includes(event.id) ? "translate-x-5" : "translate-x-0",
                        "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                      )}
                    >
                      <span
                        className={classNames(
                          selectedTextReminders.includes(event.id)
                            ? "opacity-0 duration-100 ease-out"
                            : "opacity-100 duration-200 ease-in",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                        )}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                          <path
                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                            stroke="currentColor"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                      <span
                        className={classNames(
                          selectedTextReminders.includes(event.id)
                            ? "opacity-100 duration-200 ease-in"
                            : "opacity-0 duration-100 ease-out",
                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                        )}
                        aria-hidden="true"
                      >
                        <svg className="h-3 w-3 text-indigo-600" fill="currentColor" viewBox="0 0 12 12">
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                        </svg>
                      </span>
                    </span>
                  </Switch>
                </td>
              </tr>
              <AnimatePresence>
                {expandedRows.includes(event.id) && (
                  <motion.tr
                    key={event.id}
                    initial={{ opacity: 0.3, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                    className="bg-[#e2e2db]"
                  >
                    <td colSpan={6} className="px-6 py-4">
                      <table className="w-full border-collapse border-2 bg-[#e2e2db] text-xs">
                        <thead className="bg-[#eaebe6]">
                          <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Date prefix
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Reminder Name
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Parties
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                              Responsible
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                              Calendar Reminder
                            </th>
                            <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                              Text Reminder
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {remindersAndEvents
                            .filter(reminder => reminder.parentId === event.id)
                            .map((reminder, index) => (
                              <tr key={reminder.id} className="bg-[#fafafa]">
                                <td className="whitespace-nowrap px-6 py-4 text-xs">
                                  <span className="text-sm text-gray-500">{datePrefixes[index] || ""}</span> <br />
                                  <span className="mb-2 text-xs">{formatDateWithoutTime(reminder.datetime)}</span>
                                  <br />
                                  <span
                                    className={classNames(
                                      isWeekend(reminder.datetime) ? "text-red-500" : "text-indigo-400",
                                    )}
                                  >
                                    {getDayOfWeek(reminder.datetime)}
                                  </span>
                                  <br />
                                </td>
                                <td className="whitespace-normal px-6 py-4">
                                  {reminder.title}
                                  <br />
                                  <span className="text-xs text-gray-500">{reminder.summary}</span>
                                </td>
                                <td className="flex items-center whitespace-nowrap px-6 py-4 pb-0 pt-3">
                                  {partyIcons[reminder.party]} {reminder.party}
                                </td>

                                <td className="whitespace-nowrap px-6 py-4 pt-3 text-sm">
                                  <div className="flex items-center space-x-2">
                                    {reminder.assignedUserIds.map(uid => {
                                      const user = fakeUsers.find(user => user.id === uid);
                                      return user ? (
                                        <img
                                          key={user.id}
                                          src={user.avatar}
                                          alt={user.name}
                                          width="60"
                                          height="60"
                                          className="h-6 w-6 rounded-full"
                                        />
                                      ) : null;
                                    })}
                                  </div>
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 pt-3 text-center">
                                  <Switch
                                    checked={selectedCalendarReminders.includes(reminder.id)}
                                    onChange={() => handleReminderSelect(reminder.id, "calendar")}
                                    className={classNames(
                                      selectedCalendarReminders.includes(reminder.id) ? "bg-indigo-300" : "bg-gray-200",
                                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                                    )}
                                  >
                                    <span className="sr-only">Calendar Reminder</span>
                                    <span
                                      className={classNames(
                                        selectedCalendarReminders.includes(reminder.id)
                                          ? "translate-x-5"
                                          : "translate-x-0",
                                        "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                      )}
                                    >
                                      <span
                                        className={classNames(
                                          selectedCalendarReminders.includes(reminder.id)
                                            ? "opacity-0 duration-100 ease-out"
                                            : "opacity-100 duration-200 ease-in",
                                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                                        )}
                                        aria-hidden="true"
                                      >
                                        <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                                          <path
                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </span>
                                      <span
                                        className={classNames(
                                          selectedCalendarReminders.includes(reminder.id)
                                            ? "opacity-100 duration-200 ease-in"
                                            : "opacity-0 duration-100 ease-out",
                                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                                        )}
                                        aria-hidden="true"
                                      >
                                        <svg
                                          className="h-3 w-3 text-indigo-600"
                                          fill="currentColor"
                                          viewBox="0 0 12 12"
                                        >
                                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                        </svg>
                                      </span>
                                    </span>
                                  </Switch>
                                </td>
                                <td className="whitespace-nowrap px-6 py-4 pt-3 text-center">
                                  <Switch
                                    checked={selectedTextReminders.includes(reminder.id)}
                                    onChange={() => handleReminderSelect(reminder.id, "text")}
                                    className={classNames(
                                      selectedTextReminders.includes(reminder.id) ? "bg-indigo-300" : "bg-gray-200",
                                      "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2",
                                    )}
                                  >
                                    <span className="sr-only">Text Reminder</span>
                                    <span
                                      className={classNames(
                                        selectedTextReminders.includes(reminder.id) ? "translate-x-5" : "translate-x-0",
                                        "pointer-events-none relative inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                                      )}
                                    >
                                      <span
                                        className={classNames(
                                          selectedTextReminders.includes(reminder.id)
                                            ? "opacity-0 duration-100 ease-out"
                                            : "opacity-100 duration-200 ease-in",
                                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                                        )}
                                        aria-hidden="true"
                                      >
                                        <svg className="h-3 w-3 text-gray-400" fill="none" viewBox="0 0 12 12">
                                          <path
                                            d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                          />
                                        </svg>
                                      </span>
                                      <span
                                        className={classNames(
                                          selectedTextReminders.includes(reminder.id)
                                            ? "opacity-100 duration-200 ease-in"
                                            : "opacity-0 duration-100 ease-out",
                                          "absolute inset-0 flex h-full w-full items-center justify-center transition-opacity",
                                        )}
                                        aria-hidden="true"
                                      >
                                        <svg
                                          className="h-3 w-3 text-indigo-600"
                                          fill="currentColor"
                                          viewBox="0 0 12 12"
                                        >
                                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
                                        </svg>
                                      </span>
                                    </span>
                                  </Switch>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </React.Fragment>
          ))}
        </tbody>
      </table>
      <div className="mt-4 flex items-center justify-between">
        <div>
          {Array.from({ length: pageCount }, (_, index) => (
            <button
              key={index}
              onClick={() => setCurrentPage(index + 1)}
              className={`mx-1 rounded border px-3 py-1 ${currentPage === index + 1 ? "bg-indigo-500 text-white" : "bg-white text-gray-700"}`}
            >
              {index + 1}
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between">
          <Button
            onClick={toggleModal}
            className="mr-4 rounded bg-gray-400 px-4 py-2 font-bold text-white hover:bg-blue-700"
          >
            Cancel
          </Button>
          <Link to="/calendars?matterId=ABC123&tab=2&showToast=true">
            <Button className="flex items-center space-x-2 rounded bg-indigo-500 p-4 text-white hover:bg-indigo-600">
              Send Reminders
              <SendIcon className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};
